import React, { useCallback, useEffect, useRef, useState } from 'react'
import ArtworkBackgroundControl from './ArtworkBackgroundControl'
import GuidedArtworkSizeGroup from './GuidedArtworkSizeGroup'
import {
  artworkSizeGroups,
  deriveDefaultArtworkDimensions,
  nextArtworkSizeVariantId,
  normalizeArtworkConfig,
  updateArtworkSizeGroup,
} from './artworkSource'
import { PROCESSING_ERRORS } from './processingErrors'

function sourceStatus(image) {
  if (image.processingStatus === 'failed') return 'Failed'
  if (image.processingStatus === 'pending') return 'Processing'
  return 'Ready'
}

export default function GuidedArtworkSourceCard({
  image,
  imageUrl,
  onImageError,
  config,
  arrangementSheetSize,
  onConfigChange,
  onRemove,
  onRetry,
  onBackgroundPreview,
  onBackgroundApply,
}) {
  const [removeError, setRemoveError] = useState(null)
  const [removing, setRemoving] = useState(false)
  const removeButtonRef = useRef(null)
  const sentDefaultsRef = useRef(Boolean(config?.printWidthIn && config?.printHeightIn))
  const normalized = normalizeArtworkConfig(config, image)
  const groups = artworkSizeGroups(normalized, image)
  const latestNormalizedConfigRef = useRef(normalized)
  const dimensionEditedRef = useRef(false)
  const sourceIdRef = useRef(String(image.id))
  latestNormalizedConfigRef.current = normalized

  if (sourceIdRef.current !== String(image.id)) {
    sourceIdRef.current = String(image.id)
    sentDefaultsRef.current = Boolean(config?.printWidthIn && config?.printHeightIn)
    dimensionEditedRef.current = false
  }

  const processed = image.processingStatus === 'processed' || image.processingStatus === undefined
  const failed = image.processingStatus === 'failed'
  const excluded = processed && !normalized.included
  const backgroundProcessing = image.backgroundProcessingStatus === 'processing'
  const sourceName = image.originalFilename || 'Artwork'
  const displayedStatus = excluded ? 'Not included' : backgroundProcessing ? 'Removing background' : sourceStatus(image)

  const emitConfig = useCallback(nextConfig => {
    onConfigChange?.(image.id, normalizeArtworkConfig(nextConfig, image))
  }, [image, onConfigChange])

  useEffect(() => {
    const latestConfig = latestNormalizedConfigRef.current
    if (
      sentDefaultsRef.current ||
      dimensionEditedRef.current ||
      sourceIdRef.current !== String(image.id) ||
      !processed ||
      !image.widthPx ||
      !image.heightPx ||
      latestConfig.printWidthIn != null ||
      latestConfig.printHeightIn != null
    ) return

    const defaultDimensions = deriveDefaultArtworkDimensions({ ...image, ...latestConfig })
    if (!defaultDimensions) return

    sentDefaultsRef.current = true
    emitConfig({ ...latestConfig, ...defaultDimensions })
  }, [emitConfig, image.dpi, image.heightPx, image.id, image.widthPx, processed])

  useEffect(() => {
    if (removeError) removeButtonRef.current?.focus()
  }, [removeError])

  const updateGroup = (groupIndex, changes) => {
    dimensionEditedRef.current = dimensionEditedRef.current ||
      changes.printWidthIn !== undefined || changes.printHeightIn !== undefined

    emitConfig(updateArtworkSizeGroup(normalized, groupIndex, changes, image))
  }

  const addSizeGroup = () => {
    const variants = [...(normalized.sizeVariants || []), {
      id: nextArtworkSizeVariantId(normalized),
      printWidthIn: normalized.printWidthIn,
      printHeightIn: normalized.printHeightIn,
      quantity: 1,
      included: true,
    }]
    emitConfig({ ...normalized, sizeVariants: variants, included: true })
  }

  const removeSizeGroup = groupIndex => {
    const variants = (normalized.sizeVariants || []).filter((_, index) => index !== groupIndex - 1)
    emitConfig({ ...normalized, sizeVariants: variants })
  }

  const handleRemove = async () => {
    if (!window.confirm(`Remove ${sourceName} and every copy from this gang sheet?`)) return
    setRemoving(true)
    setRemoveError(null)
    try {
      await onRemove?.(image.id)
    } catch (error) {
      setRemoveError(error?.message || 'Could not remove this design. Please try again.')
      setRemoving(false)
    }
  }

  return (
    <article
      className={`guided-source-card${failed ? ' guided-source-card--failed' : ''}${excluded ? ' guided-source-card--excluded' : ''}`}
      data-testid={`guided-source-${image.id}`}
      data-guided-source-card
    >
      <div className="guided-source-card__identity">
        <div className="guided-source-card__thumbnail">
          {imageUrl ? <img crossOrigin="anonymous" src={imageUrl} onError={onImageError} alt="" /> : <span aria-hidden="true" />}
        </div>
        <div className="guided-source-card__title">
          <h3 title={image.originalFilename}>{sourceName}</h3>
          <span className={`guided-source-card__status guided-source-card__status--${displayedStatus.toLowerCase().replace(/\s+/g, '-')}`}>
            {displayedStatus}
          </span>
        </div>
        <button
          ref={removeButtonRef}
          className="guided-source-card__remove"
          type="button"
          disabled={removing}
          onClick={handleRemove}
          aria-label={`Remove design ${sourceName}`}
        >
          {removing ? 'Removing…' : 'Remove design'}
        </button>
      </div>

      {failed ? (
        <div className="guided-source-card__failure" role="alert">
          <p>{PROCESSING_ERRORS[image.processingError] || 'This artwork could not be processed.'}</p>
          <button
            type="button"
            className="gs-btn-secondary"
            data-guided-source-focus-target
            aria-label={`Retry upload for ${sourceName}`}
            onClick={() => onRetry?.(image)}
          >
            Retry upload
          </button>
        </div>
      ) : (
        <>
          {excluded && (
            <div className="guided-source-card__excluded" role="status">
              <p>This design and all of its sizes will not be placed in the generated sheet.</p>
              <button
                type="button"
                className="gs-btn-secondary"
                data-guided-source-focus-target
                aria-label={`Include design ${sourceName}`}
                onClick={() => emitConfig({ ...normalized, included: true })}
              >
                Include design
              </button>
            </div>
          )}

          <div className="guided-source-card__size-groups">
            {groups.map((group, index) => (
              <GuidedArtworkSizeGroup
                key={group.id}
                image={image}
                imageUrl={imageUrl}
                config={group}
                groupNumber={index + 1}
                groupCount={groups.length}
                arrangementSheetSize={arrangementSheetSize}
                disabled={!processed}
                onChange={changes => updateGroup(index, changes)}
                onRemove={index === 0 ? undefined : () => removeSizeGroup(index)}
              />
            ))}
          </div>

          <button
            type="button"
            className="gs-btn-secondary guided-source-card__add-size"
            disabled={!processed}
            onClick={addSizeGroup}
          >
            Use design again
          </button>
          <p className="guided-source-card__add-size-help">
            Add another print size and quantity without uploading this file again.
          </p>

          {processed && (
            <ArtworkBackgroundControl
              image={image}
              onPreview={onBackgroundPreview}
              onApply={onBackgroundApply}
            />
          )}
        </>
      )}

      {removeError && <p className="guided-source-card__warning" role="alert">{removeError}</p>}
    </article>
  )
}
